
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function WorkWithUs() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleBookClass = () => {
    // Trigger Readdy Agent widget for booking
    const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (widget) {
      widget.click();
    } else {
      // Fallback to contact page
      navigate('/contact');
    }
  };

  const positions = [
    {
      title: t('workWithUs.positions.mathTeacher'),
      type: t('workWithUs.employment.fullTime') || 'Full-time',
      location: t('workWithUs.location.online') || 'Online',
      requirements: [
        t('workWithUs.requirements.education'),
        t('workWithUs.requirements.experience'),
        t('workWithUs.requirements.knowledge'),
        t('workWithUs.requirements.online')
      ],
      responsibilities: [
        t('workWithUs.responsibilities.individual') || 'Conduct individual and group lessons',
        t('workWithUs.responsibilities.materials') || 'Prepare learning materials',
        t('workWithUs.responsibilities.assessment') || 'Assess student progress',
        t('workWithUs.responsibilities.parents') || 'Interact with student parents'
      ]
    },
    {
      title: t('workWithUs.positions.englishTeacher'),
      type: t('workWithUs.employment.fullTime') || 'Full-time',
      location: t('workWithUs.location.online') || 'Online',
      requirements: [
        t('workWithUs.positions.englishReq1'),
        t('workWithUs.requirements.englishLevel') || 'English level not lower than C1',
        t('workWithUs.positions.englishReq2'),
        t('workWithUs.requirements.online')
      ],
      responsibilities: [
        t('workWithUs.responsibilities.interactive') || 'Conduct interactive English lessons',
        t('workWithUs.responsibilities.individual') || 'Develop individual learning programs',
        t('workWithUs.responsibilities.exams') || 'Prepare for international exams',
        t('workWithUs.responsibilities.monitoring') || 'Monitor student success'
      ]
    },
    {
      title: t('workWithUs.positions.methodist') || 'Methodologist',
      type: t('workWithUs.employment.partTime') || 'Part-time',
      location: t('workWithUs.location.remote') || 'Remote',
      requirements: [
        t('workWithUs.requirements.pedagogical') || 'Pedagogical education',
        t('workWithUs.requirements.curriculum') || 'Experience in curriculum development',
        t('workWithUs.requirements.technology') || 'Knowledge of modern educational technologies',
        t('workWithUs.requirements.analytical') || 'Analytical skills'
      ],
      responsibilities: [
        t('workWithUs.responsibilities.development') || 'Develop learning materials',
        t('workWithUs.responsibilities.analysis') || 'Analyze effectiveness of learning programs',
        t('workWithUs.responsibilities.support') || 'Support teachers',
        t('workWithUs.responsibilities.quality') || 'Control quality of educational process'
      ]
    }
  ];

  const benefits = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: t('workWithUs.benefits.salary'),
      description: t('workWithUs.benefits.salaryDesc') || 'Competitive salary and bonus system'
    },
    {
      icon: 'ri-time-line',
      title: t('workWithUs.benefits.schedule'),
      description: t('workWithUs.benefits.scheduleDesc') || 'Ability to plan your own working hours'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: t('workWithUs.benefits.development'),
      description: t('workWithUs.benefits.developmentDesc') || 'Free professional development courses'
    },
    {
      icon: 'ri-team-line',
      title: t('workWithUs.benefits.team'),
      description: t('workWithUs.benefits.teamDesc') || 'Colleague support and comfortable work atmosphere'
    },
    {
      icon: 'ri-computer-line',
      title: t('workWithUs.benefits.technology'),
      description: t('workWithUs.benefits.technologyDesc') || 'Work with the latest educational platforms'
    },
    {
      icon: 'ri-heart-line',
      title: t('workWithUs.benefits.package') || 'Social Package',
      description: t('workWithUs.benefits.packageDesc') || 'Health insurance and additional benefits'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section with Motion Video Banner */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover object-top"
            poster="https://readdy.ai/api/search-image?query=Bright%20vibrant%20professional%20teachers%20collaborating%20in%20modern%20educational%20environment%2C%20diverse%20team%20of%20educators%20working%20together%20with%20laptops%20and%20educational%20materials%2C%20bright%20office%20space%20with%20natural%20lighting%2C%20professional%20teamwork%20in%20educational%20setting%2C%20modern%20workplace%20with%20emerald%20green%20and%20teal%20accents%2C%20high%20quality%20professional%20collaboration%20scene%20with%20teachers%20planning%20lessons%20and%20sharing%20ideas&width=1920&height=1080&seq=work-with-us-hero-video-poster-v2&orientation=landscape"
          >
            <source src="https://videos.pexels.com/video-files/7092364/7092364-uhd_2560_1440_25fps.mp4" type="video/mp4" />
            <source src="https://videos.pexels.com/video-files/7092364/7092364-hd_1920_1080_25fps.mp4" type="video/mp4" />
            <source src="https://videos.pexels.com/video-files/7092364/7092364-sd_640_360_25fps.mp4" type="video/mp4" />
          </video>
          {/* Overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/30 via-emerald-500/20 to-teal-600/30"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight drop-shadow-2xl">
              <span className="bg-gradient-to-r from-white via-yellow-100 to-white bg-clip-text text-transparent">
                {t('workWithUs.title')}
              </span>
            </h1>
            <p className="text-xl md:text-2xl lg:text-3xl mb-12 font-medium leading-relaxed drop-shadow-xl max-w-4xl mx-auto">
              {t('workWithUs.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Button 
                size="lg" 
                className="px-12 py-6 text-xl font-bold border-2 border-white/30 rounded-xl"
                onClick={() => navigate('/contact')}
              >
                <i className="ri-send-plane-line mr-3 text-xl"></i>
                {t('workWithUs.sendResume') || 'Send Resume'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-white to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-6">
              {t('workWithUs.whyJoin')}
            </h2>
            <p className="text-xl text-gray-600 font-medium">
              {t('workWithUs.whyJoinDesc') || 'MathLish is a place where your talent will flourish to its fullest'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 border-2 border-emerald-100 hover:border-emerald-200">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 mx-auto shadow-lg">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-6">
              {t('workWithUs.openPositions')}
            </h2>
            <p className="text-xl text-gray-600 font-medium">
              {t('workWithUs.findPosition') || 'Find your perfect position in our team'}
            </p>
          </div>
          
          <div className="space-y-8">
            {positions.map((position, index) => (
              <div key={index} className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-10 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 border-2 border-emerald-100 hover:border-emerald-200">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {position.title}
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <i className="ri-briefcase-line text-emerald-600 mr-3 text-lg"></i>
                        <span className="text-gray-700 font-medium">{position.type}</span>
                      </div>
                      <div className="flex items-center">
                        <i className="ri-map-pin-line text-emerald-600 mr-3 text-lg"></i>
                        <span className="text-gray-700 font-medium">{position.location}</span>
                      </div>
                    </div>
                    <Button 
                      className="mt-6 w-full"
                      onClick={() => navigate('/contact')}
                    >
                      <i className="ri-send-plane-line mr-3"></i>
                      {t('workWithUs.apply') || 'Apply Now'}
                    </Button>
                  </div>
                  
                  <div className="lg:col-span-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">
                          <i className="ri-checkbox-circle-line text-emerald-600 mr-2"></i>
                          {t('workWithUs.requirements')}:
                        </h4>
                        <ul className="space-y-3">
                          {position.requirements.map((req, reqIndex) => (
                            <li key={reqIndex} className="flex items-start">
                              <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                              <span className="text-gray-700">{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">
                          <i className="ri-task-line text-teal-600 mr-2"></i>
                          {t('workWithUs.responsibilities') || 'Responsibilities'}:
                        </h4>
                        <ul className="space-y-3">
                          {position.responsibilities.map((resp, respIndex) => (
                            <li key={respIndex} className="flex items-start">
                              <div className="w-2 h-2 bg-teal-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                              <span className="text-gray-700">{resp}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('workWithUs.applicationProcess') || 'Application Process'}
            </h2>
            <p className="text-xl text-white/95 font-medium">
              {t('workWithUs.processDesc') || 'Simple and transparent selection process'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '1', title: t('workWithUs.steps.submit') || 'Submit Resume', description: t('workWithUs.steps.submitDesc') || 'Send your resume through the contact form' },
              { step: '2', title: t('workWithUs.steps.interview') || 'Interview', description: t('workWithUs.steps.interviewDesc') || 'Online interview with HR manager and methodologist' },
              { step: '3', title: t('workWithUs.steps.demo') || 'Demo Lesson', description: t('workWithUs.steps.demoDesc') || 'Conduct a demonstration lesson' },
              { step: '4', title: t('workWithUs.steps.contract') || 'Contract', description: t('workWithUs.steps.contractDesc') || 'Sign contract and start working' }
            ].map((step, index) => (
              <div key={index} className="text-center text-white">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-6 mx-auto border-2 border-white/30 shadow-xl">
                  <span className="text-2xl font-bold">{step.step}</span>
                </div>
                <h3 className="text-xl font-bold mb-4">{step.title}</h3>
                <p className="text-white/90 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-6">
            {t('workWithUs.readyToJoin') || 'Ready to join our team?'}
          </h2>
          <p className="text-xl text-gray-600 mb-8 font-medium">
            {t('workWithUs.sendResumeToday') || 'Send your resume and become part of MathLish today!'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/contact')}
            >
              <i className="ri-mail-send-line mr-3"></i>
              {t('workWithUs.sendResume') || 'Send Resume'}
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white"
              onClick={handleBookClass}
            >
              <i className="ri-question-answer-line mr-3"></i>
              {t('workWithUs.askQuestion') || 'Ask Question'}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
